import { memo } from 'react'
import Box from './Box'
import { SIX_PX } from '../enums/Spacings'
import styled from '@emotion/styled'
import { BorderRadixes } from '../enums/BorderRadixes'
import GrooverText from './GrooverText'
import useAppState from '../hooks/useAppState'

const ACTIVE_TEXT_STYLE = { fontWeight: 700 } as const
const COMPLETED_TEXT_STYLE = { fontWeight: 500 } as const

const RegisterStep = ({
  index,
  step,
  stepNumber,
  isRegistrationCompleted,
  isCurrentlyOpen = false,
  isDisabled = false,
  onOpenStep
}: {
  index: number
  step: { textKey: string; isActive: boolean; isCompleted: boolean }
  stepNumber: number
  isRegistrationCompleted: boolean
  isCurrentlyOpen?: boolean
  isDisabled?: boolean
  onOpenStep?: (step: number) => void
}) => {
  const { state } = useAppState()
  const language = state.selectedLanguage
  const isActive = !isRegistrationCompleted && step.isActive
  const isCompleted = isRegistrationCompleted || step.isCompleted
  // Only allow navigation via the header when registration is fully done (or returning via URL).
  // Using isCompleted instead would allow mid-flow backward navigation, which strands the user
  // since forward steps would then lose their isCompleted state and become unclickable.
  const isClickable = isRegistrationCompleted && !isDisabled && !!onOpenStep

  const textStyle = isActive ? ACTIVE_TEXT_STYLE : isCompleted ? COMPLETED_TEXT_STYLE : undefined

  return (
    <Box
      direction="row"
      align="center"
      gap={SIX_PX}
      onClick={isClickable ? () => onOpenStep(stepNumber) : undefined}
      style={isClickable ? { cursor: 'pointer' } : undefined}
    >
      <StepCircle
        align="center"
        justify="center"
        isActive={isActive}
        isCompleted={isCompleted}
        isCurrentlyOpen={isCurrentlyOpen && isCompleted}
      >
        {index + 1}
      </StepCircle>
      <TextBox isActive={isActive} isCompleted={isCompleted}>
        <GrooverText textKey={step.textKey} language={language} variant="body.small" style={textStyle} />
      </TextBox>
    </Box>
  )
}

const stepOnlyProps = ['isActive', 'isCompleted', 'isCurrentlyOpen']

const StepCircle = styled(Box, {
  shouldForwardProp: (prop) => !stepOnlyProps.includes(prop)
})<{ isActive: boolean; isCompleted: boolean; isCurrentlyOpen: boolean }>`
  width: 30px;
  height: 30px;
  color: white;
  flex-shrink: 0;
  font-weight: bold;
  border-radius: ${BorderRadixes.circle};
  background-color: ${({ isActive, isCompleted }) => (isActive ? '#6AAB37' : isCompleted ? '#6AAB37' : '#d9d9d9')};
  box-shadow: ${({ isCurrentlyOpen }) => (isCurrentlyOpen ? '0 0 0 3px white, 0 0 0 5px #6AAB37' : 'none')};
`

const TextBox = styled(Box, {
  shouldForwardProp: (prop) => !stepOnlyProps.includes(prop)
})<{ isActive: boolean; isCompleted: boolean }>`
  @media (max-width: 574px) {
    display: none;
  }
  opacity: ${({ isActive, isCompleted }) => (isActive || isCompleted ? 1 : 0.5)};
`

export default memo(RegisterStep)
